<template>
  <BaseModal
    :value="dialogState"
    :modal-title="
      isCreateForm
        ? $t('workflow.targetStatus.createAndEditForm.createTitle')
        : $t('workflow.targetStatus.createAndEditForm.editTitle')
    "
    @click:outside="reset"
    @keydown.esc="reset"
    @close="reset"
  >
    <template #modal>
      <i18n
        class="text d-block my-2"
        path="workflow.targetStatus.createAndEditForm.sourceStatusLabel"
      >
        <template #statusName>
          <span class="blue-text" v-text="`${status && status.name}`" />
        </template>
      </i18n>
      <i18n
        v-if="!isCreateForm && activeTargetStatus.statusTo"
        class="text d-block my-2"
        path="workflow.targetStatus.createAndEditForm.targetStatusLabel"
      >
        <template #statusName>
          <span
            class="blue-text"
            v-text="`${activeTargetStatus.statusTo.name}`"
          />
        </template>
      </i18n>
      <v-form ref="form" @submit.prevent="submit()">
        <BaseSelect
          v-if="isCreateForm"
          v-model="form.targetStatus"
          solo
          outlined
          :items="allowedTargetStatuses"
          :placeholder="
            $t('workflow.targetStatus.createAndEditForm.targetStatus') + ' *'
          "
          item-text="name"
          item-value="id"
          :error-messages="targetStatusErrors"
          @change="$v.form.targetStatus.$touch()"
          @blur="$v.form.targetStatus.$touch()"
        />

        <BaseTextField
          v-model="form.message"
          :placeholder="
            $t('workflow.targetStatus.createAndEditForm.label') + ' *'
          "
          :error-messages="messageErrors"
          :has-error="messageErrors !== ''"
          @changed="$v.form.message.$touch()"
          @blur="$v.form.message.$touch()"
        />
      </v-form>
    </template>

    <template #actions>
      <BaseButton
        text
        class="mx-2 ml-auto"
        type="secondary"
        color="#707080"
        @click="reset"
      >
        {{ $t("utils.cancel") }}
      </BaseButton>
      <BaseButton type="primary" :disabled="$v.$invalid" @click="submit">
        {{ $t("folder.statusForm.validate") }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import { validationMixin } from "vuelidate";
import {
  createOrganizationWorkflowStatusTargetStatus,
  updateOrganizationWorkflowStatusTargetStatus,
} from "@/modules/Admin/Services/workflow.service";
import { required } from "vuelidate/lib/validators";

export default {
  name: "WorkflowStatusTargetStatusCreateAndEditDialog",
  mixins: [validationMixin],
  props: {
    dialogState: {
      type: Boolean,
    },
    workflow: {
      type: Object,
      required: true,
    },
    status: {
      type: Object,
      required: true,
    },
    targetStatuses: {
      type: Array,
      required: true,
    },
    activeTargetStatus: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      form: {
        targetStatus: null,
        message: null,
      },
    };
  },
  validations() {
    if (this.isCreateForm) {
      return {
        form: {
          targetStatus: {
            required,
          },
          message: {
            required,
          },
        },
      };
    } else {
      return {
        form: {
          message: {
            required,
          },
        },
      };
    }
  },
  computed: {
    allowedTargetStatuses() {
      return this.workflow?.statuses
        .filter((s) => s.id !== this.status?.id)
        .filter(
          (s) => !this.targetStatuses.some((ts) => ts.statusTo.id == s.id)
        );
    },
    isCreateForm() {
      return !this.activeTargetStatus;
    },
    //#region Form Validation Errors
    targetStatusErrors() {
      if (!this.$v.form.targetStatus.$dirty) {
        return "";
      }
      if (!this.$v.form.targetStatus.required) {
        return this.$t("utils.mandatory");
      }
      return "";
    },
    messageErrors() {
      if (!this.$v.form.message.$dirty) {
        return "";
      }
      if (!this.$v.form.message.required) {
        return this.$t("utils.mandatory");
      }
      return "";
    },
    //#endregion
  },
  watch: {
    dialogState(newVal) {
      if (newVal) {
        this.initialize();
      }
    },
  },
  methods: {
    reset() {
      this.form = {
        targetStatus: null,
        message: null,
      };
      this.$v.$reset();
      this.$emit("close");
    },
    initialize() {
      if (this.isCreateForm) return;
      this.form.targetStatus = this.activeTargetStatus.statusTo.id;
      this.form.message = this.activeTargetStatus.label;
    },
    submit() {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid) {
        if (this.isCreateForm) {
          this.create();
        } else {
          this.edit();
        }
        this.reset();
      }
    },
    async create() {
      const payload = {
        label: this.form.message,
        status_to: this.form.targetStatus,
      };
      try {
        const res = await createOrganizationWorkflowStatusTargetStatus(
          this.$route.params.organizationId,
          this.$route.params.workflowId,
          this.status.id,
          payload
        );
        const newTargetStatus = res.data;
        this.$emit("targetStatusCreated", newTargetStatus);
        // Display TheSnackbar
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.targetStatus.createAndEditForm.successCreate"
          ),
          type: "SUCCESS",
        });
      } catch {
        // Display TheSnackbar
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async edit() {
      const payload = {
        label: this.form.message,
      };
      try {
        const res = await updateOrganizationWorkflowStatusTargetStatus(
          this.$route.params.organizationId,
          this.$route.params.workflowId,
          this.status.id,
          this.activeTargetStatus.id,
          payload
        );
        const targetStatus = res.data;
        this.$emit("targetStatusUpdated", targetStatus);
        // Display TheSnackbar
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.targetStatus.createAndEditForm.successEdit"
          ),
          type: "SUCCESS",
        });
      } catch {
        // Display TheSnackbar
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
  },
};
</script>
<style scoped>
.text {
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
}
.blue-text {
  color: #2536cc;
}
</style>
